import axios from 'axios';
import { authEvents } from '../utils/authEvents';
import { getAuthHeaders } from './inquiries'; // Reuse existing config

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

export const fetchWithAuth = async (url, options = {}, { retried = false } = {}) => {
    const doFetch = async () => {
        const headers = {
            ...options.headers,
            ...getAuthHeaders()
        };

        // Ensure Content-Type is json unless FormData (which has no content-type set manually usually)
        if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
            headers['Content-Type'] = 'application/json';
        }

        const res = await fetch(url, { ...options, headers });

        // Only a fresh 401 starts the re-login flow; a request that already came
        // back from a retry is passed through so we cannot loop forever.
        if (res.status === 401 && !retried) {
            // Unauthorized - Trigger Re-auth
            console.warn('[API] 401 Unauthorized. Triggering re-login flow.');

            return waitForReauth().then(() => {
                // Retry once after the user logs back in
                return fetchWithAuth(url, options, { retried: true });
            });
        }

        return res;
    };

    return doFetch();
};

/**
 * Parks the caller until the user logs in again.
 * The queue is shared with the axios instance below so that a burst of 401s
 * (fetch + axios mixed) only ever opens one re-login modal.
 */
const waitForReauth = () => new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });

    if (!isRefreshing) {
        isRefreshing = true;
        authEvents.emit('sessionExpired');

        // Listener for successful login
        const onLoginSuccess = () => {
            isRefreshing = false;
            authEvents.off('loginSuccess', onLoginSuccess);
            // Retry original request (and others in queue)
            processQueue(null, true);
        };

        authEvents.on('loginSuccess', onLoginSuccess);
    }
});

/**
 * axios counterpart of fetchWithAuth.
 * - request interceptor attaches the bearer token (getAuthHeaders)
 * - response interceptor turns a 401 into the same re-login + retry flow
 *
 * Use this for admin-only endpoints. Public endpoints (3-minute estimate
 * sessions/questionnaire, inquiry submission, public content) must keep using
 * plain `axios` so that anonymous visitors are never prompted to log in.
 */
export const authAxios = axios.create();

authAxios.interceptors.request.use((config) => {
    const authHeaders = getAuthHeaders();
    Object.entries(authHeaders).forEach(([key, value]) => {
        if (config.headers && typeof config.headers.set === 'function') {
            config.headers.set(key, value);
        } else {
            config.headers = { ...config.headers, [key]: value };
        }
    });
    return config;
});

authAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const config = error?.config;

        // Only a fresh 401 starts the re-login flow; a request that already
        // came back from a retry is passed through so we cannot loop forever.
        if (status !== 401 || !config || config._retriedAfterReauth) {
            return Promise.reject(error);
        }

        console.warn('[API] 401 Unauthorized. Triggering re-login flow.');
        config._retriedAfterReauth = true;

        return waitForReauth().then(() => authAxios(config));
    },
);
